import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/env';

const supabaseRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${SUPABASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
};

export class TelecallerService {
  static async getTelecallerStats(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_stats`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      return { success: true, data: (Array.isArray(data) ? data[0] : data) || {}, error: null };
    } catch (error) {
      console.error('Error fetching telecaller stats:', error);
      return { success: false, data: {}, error: error.message };
    }
  }

  static async getAssignedLeads(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_assigned_leads`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching assigned leads:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getLeadsHistory(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_leads_history`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching leads history:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getBookedOrders(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_booked_orders`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching booked orders:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async updateLeadStatus(leadId, status, notes) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_update_lead_status`, {
        method: 'POST',
        body: JSON.stringify({ p_lead_id: leadId, p_status: status, p_notes: notes }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error updating lead status:', error);
      return { success: false, error: error.message };
    }
  }

  static async confirmLeads(leadIds, telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_confirm_leads`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_lead_ids: leadIds, 
          p_telecaller_id: telecallerId 
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error confirming leads:', error);
      return { success: false, error: error.message };
    }
  }

  static async logCall(leadId, telecallerId, callStatus, notes, duration, recordingUrl) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_log_call`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_lead_id: leadId,
          p_telecaller_id: telecallerId,
          p_call_status: callStatus,
          p_notes: notes || '',
          p_duration_seconds: duration || 0,
          p_recording_url: recordingUrl || null,
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error logging call:', error);
      return { success: false, error: error.message };
    }
  }

  static async getCallbackLeads(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_callback_leads`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching callback leads:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getDialerLeads(telecallerId, dialerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_dialer_leads`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_telecaller_id: telecallerId,
          p_dialer_id: dialerId 
        }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching dialer leads:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getNotAnswerLeads(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_not_answer_leads`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching not answer leads:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getNotInterestedLeads(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_not_interested_leads`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching not interested leads:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getConfirmationLeads(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_confirmation_leads`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching confirmation leads:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getCallLogs(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_call_logs`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching call logs:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async claimLead(leadId, telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_claim_lead`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_lead_id: leadId,
          p_telecaller_id: telecallerId 
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error claiming lead:', error);
      return { success: false, error: error.message };
    }
  }

  static async unassignLead(leadId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_unassign_lead`, {
        method: 'POST',
        body: JSON.stringify({ p_lead_id: leadId }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error unassigning lead:', error);
      return { success: false, error: error.message };
    }
  }

  // =========================
  // WFH Pool helpers
  // =========================
  static async getMyTeamLeadId(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_get_my_teamlead_id`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      const row = Array.isArray(data) ? data[0] : data;
      return { success: true, data: row || {}, error: null };
    } catch (error) {
      console.error('Error fetching team lead id:', error);
      return { success: false, data: {}, error: error.message };
    }
  }

  static async claimNextWfhLead(telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_claim_next_wfh_lead`, {
        method: 'POST',
        body: JSON.stringify({ p_telecaller_id: telecallerId }),
      });
      const row = Array.isArray(data) ? data[0] : data;
      return { success: true, data: row || null, error: null };
    } catch (error) {
      console.error('Error claiming next WFH lead:', error);
      return { success: false, data: null, error: error.message };
    }
  }

  static async releaseWfhLead(leadId, telecallerId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_release_wfh_lead`, {
        method: 'POST',
        body: JSON.stringify({ p_lead_id: leadId, p_telecaller_id: telecallerId }),
      });
      const row = Array.isArray(data) ? data[0] : data;
      return { success: true, data: row || {}, error: null };
    } catch (error) {
      console.error('Error releasing WFH lead:', error);
      return { success: false, data: {}, error: error.message };
    }
  }

  static async setWfhDisposition(telecallerId, leadId, disposition, notes) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_set_wfh_disposition`, {
        method: 'POST',
        body: JSON.stringify({
          p_telecaller_id: telecallerId,
          p_lead_id: leadId,
          p_disposition: disposition,
          p_notes: notes || null,
        }),
      });
      const row = Array.isArray(data) ? data[0] : data;
      return { success: true, data: row || {}, error: null };
    } catch (error) {
      console.error('Error saving WFH disposition:', error);
      return { success: false, data: {}, error: error.message };
    }
  }

  // =========================
  // Sarv click-to-call (server-side proxy)
  // =========================
  static async sarvClickToCall(fromNumber, toNumber, opts = {}) {
    try {
      const base = `${SUPABASE_URL}/functions/v1`;
      const headers = {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      };
      const payload = { from: fromNumber, to: toNumber };
      if (opts.lead_id) payload.lead_id = opts.lead_id;
      if (opts.telecaller_id) payload.telecaller_id = opts.telecaller_id;
      // Prefer gateway (DB-driven routing); fall back to legacy Sarv-only function if not deployed
      let resp = await fetch(`${base}/click-to-call-gateway`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (resp.status === 404) {
        resp = await fetch(`${base}/sarv-click-to-call`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ from: fromNumber, to: toNumber }),
        });
      }

      // Backward-compatible fallback (older deployments)
      if (!resp.ok) {
        resp = await fetch(`/api/sarv-click-to-call`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ from: fromNumber, to: toNumber }),
        });
      }
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.error || 'Click-to-call failed');
      }
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Sarv click-to-call error:', error);
      return { success: false, data: null, error: error.message };
    }
  }

  static async logWfhCallAttempt(telecallerId, leadId, status, durationSeconds, sarvResponse) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_log_wfh_call_attempt`, {
        method: 'POST',
        body: JSON.stringify({
          p_telecaller_id: telecallerId,
          p_lead_id: leadId,
          p_status: status,
          p_duration_seconds: durationSeconds ?? null,
          p_sarv_response: sarvResponse ?? null,
        }),
      });
      const row = Array.isArray(data) ? data[0] : data;
      return { success: true, data: row || {}, error: null };
    } catch (error) {
      console.error('Error logging call attempt:', error);
      return { success: false, data: {}, error: error.message };
    }
  }

  static async createMasterOrder(orderData) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/telecaller_create_master_order`, {
        method: 'POST',
        body: JSON.stringify({
          p_lead_id: orderData.leadId,
          p_customer_name: orderData.customerName,
          p_vehicle_model: orderData.vehicleModel,
          p_vehicle_number: orderData.vehicleNumber,
          p_contact_number: orderData.contactNumber,
          p_address: orderData.address,
          p_pincode: orderData.pincode,
          p_cod_amount: orderData.codAmount,
          p_payment_mode: orderData.paymentMode,
          p_telecaller_id: orderData.telecallerId,
          p_team_lead_id: orderData.teamLeadId,
          p_recording_url: orderData.recordingUrl || null,
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error creating master order:', error);
      return { success: false, error: error.message };
    }
  }
}

